import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { usePreview } from '../contexts/PreviewContext';

// The supplier portal reads only what Cloud Functions build for the signed-in
// supplier (functions/supplierViews.js): supplierAccess/{email} and their
// supplierViews. An admin preview reads the same documents, or — when
// previewing unsaved permission changes — views the preview built with them.

function myEmail(currentUser) {
  return (currentUser?.email || '').trim().toLowerCase();
}

/** { supplierIds, supplierName } for the signed-in supplier, or null until known. */
export function useSupplierAccess() {
  const { currentUser } = useAuth();
  const preview = usePreview();
  const email = myEmail(currentUser);
  const [access, setAccess] = useState(undefined);

  useEffect(() => {
    if (preview?.supplier) {
      setAccess({ supplierIds: [preview.supplier.id], supplierName: preview.supplier.name || '' });
      return undefined;
    }
    if (!email) return undefined;
    return onSnapshot(doc(db, 'supplierAccess', email),
      (snap) => setAccess(snap.exists() ? snap.data() : null),
      () => setAccess(null));
  }, [email, preview?.supplier]);

  return access;
}

/** The supplier's views, all tours or one. `loading` until the first answer. */
export function useSupplierViews(requestId = null) {
  const { currentUser } = useAuth();
  const preview = usePreview();
  const email = myEmail(currentUser);
  const supplied = preview?.supplierViews || null;
  const [live, setLive] = useState(null);

  useEffect(() => {
    if (supplied || !email) return undefined;
    const constraints = [where('supplierEmail', '==', email)];
    if (requestId) constraints.push(where('requestId', '==', requestId));
    return onSnapshot(query(collection(db, 'supplierViews'), ...constraints),
      (snap) => setLive(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => { console.error('Could not load supplier views', err); setLive([]); });
  }, [email, requestId, supplied]);

  if (supplied) {
    return { loading: false, views: requestId ? supplied.filter((v) => v.requestId === requestId) : supplied };
  }
  return { loading: live === null, views: live || [] };
}

const byId = (lists) => {
  const map = new Map();
  lists.flat().forEach((x) => { if (x && !map.has(x.id)) map.set(x.id, x); });
  return [...map.values()];
};

/**
 * One tour's views as one. A login usually has one supplier record; when it
 * has several on the same tour, their activities, bookings and approvals add
 * up and the widest permissions apply.
 */
export function mergeViews(views) {
  if (!views || views.length === 0) return null;
  if (views.length === 1) return views[0];
  const score = (v) => Object.values(v.permissions || {}).filter(Boolean).length;
  const base = [...views].sort((a, b) => score(b) - score(a))[0];
  const permissions = {};
  views.forEach((v) => Object.entries(v.permissions || {}).forEach(([k, on]) => { permissions[k] = permissions[k] || on; }));
  return {
    ...base,
    permissions,
    myItems: byId(views.map((v) => v.myItems || [])),
    bookings: byId(views.map((v) => v.bookings || [])),
    approvals: byId(views.map((v) => v.approvals || [])),
  };
}
